// Quit habits ("smoke-free day"): days free and money saved. Shared by backend, web and native.

export type QuitKind = 'smoke' | 'vape' | 'alcohol' | 'free'

export type QuitHabitInput = {
  title: string
  icon?: string | null
  quit?: boolean | null
}

const ICON_KIND: Record<string, QuitKind> = { 'cigarette-off': 'smoke', 'wine-off': 'alcohol' }

// Vape before smoke: "electronic cigarette" also names a cigarette.
const SUBJECTS: [QuitKind, RegExp][] = [
  ['vape', /\bvap(e|es|ing|ear|er|oter)\b|e-?cig|电子烟|電子タバコ|ベイプ/i],
  [
    'smoke',
    /smok|cigar|tobacco|nicotine|fumar|fumer|fumo|tabac|tabaco|タバコ|たばこ|煙草|喫煙|禁煙|烟/i,
  ],
  [
    'alcohol',
    /alcohol|álcool|alcool|booze|\bbeer|\bwine|\bdrink|\bsober|beber|bebida|cerveza|boire|休肝日|お酒|飲酒|禁酒|断酒|酒/i,
  ],
]

// Words that make a title about abstaining rather than doing.
const QUIT_WORDS =
  /\b(quit|quitting|stop|stopped|no|non|free|without|sober|sobriety|zero|cut|dejar|sin|parar|libre|sans|arr[eê]ter|pas de|ne pas|sem|largar|livre)\b|não|-free|ない|やめ|止め|禁|断|控え|休肝日|不|戒|无|停/i

// Titles that only ever mean abstaining, whatever else they say.
const ALWAYS_QUIT = /\bsober|sobriety|禁煙|禁酒|休肝日|断酒|戒烟|戒酒|不吸烟|不抽烟|不喝酒/i

function titleKind(title: string): QuitKind | null {
  return SUBJECTS.find(([, re]) => re.test(title))?.[0] ?? null
}

// Null when the habit isn't a quit habit. `quit` (the user's toggle) wins over detection.
export function quitKind(h: QuitHabitInput): QuitKind | null {
  if (h.quit === false) return null
  const title = h.title.split('///')[0]
  const fromTitle = titleKind(title)
  const fromIcon = h.icon ? (ICON_KIND[h.icon] ?? null) : null
  if (h.quit) return fromTitle ?? fromIcon ?? 'free'
  if (fromIcon) return fromTitle ?? fromIcon
  if (fromTitle && (ALWAYS_QUIT.test(title) || QUIT_WORDS.test(title))) return fromTitle
  return null
}

export const isQuitHabit = (h: QuitHabitInput) => quitKind(h) !== null

export const QUIT_COST_MAX = 1_000_000

export function cleanDailyCost(n: number | null | undefined): number | undefined {
  if (n == null || !Number.isFinite(n) || n <= 0) return undefined
  return Math.min(QUIT_COST_MAX, Math.round(n * 100) / 100)
}

export function moneySaved(freeDays: number, dailyCost: number | null | undefined) {
  return dailyCost ? Math.round(freeDays * dailyCost * 100) / 100 : null
}

// Region → currency for the cost input's default; anything unlisted falls back to USD.
const EURO = 'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK'.split(' ')
const REGION_CURRENCY: Record<string, string> = {
  ...Object.fromEntries(EURO.map((r) => [r, 'EUR'])),
  JP: 'JPY',
  US: 'USD',
  GB: 'GBP',
  CN: 'CNY',
  TW: 'TWD',
  HK: 'HKD',
  SG: 'SGD',
  KR: 'KRW',
  IN: 'INR',
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'NZD',
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  CZ: 'CZK',
  BR: 'BRL',
  MX: 'MXN',
  AR: 'ARS',
  CL: 'CLP',
  CO: 'COP',
  PE: 'PEN',
  ZA: 'ZAR',
  PH: 'PHP',
  TH: 'THB',
  ID: 'IDR',
  MY: 'MYR',
  VN: 'VND',
  TR: 'TRY',
  IL: 'ILS',
  AE: 'AED',
  SA: 'SAR',
}

export function regionOf(locale: string): string | null {
  try {
    return new Intl.Locale(locale).maximize().region ?? null
  } catch {
    return /[-_]([A-Z]{2})\b/.exec(locale)?.[1] ?? null
  }
}

// First locale that names a known region wins (e.g. ["en", "ja-JP"] on a Japanese Mac).
export function currencyForLocales(locales: readonly string[]): string {
  for (const l of locales) {
    if (!/[-_][A-Za-z]{2}\b/.test(l)) continue
    const c = REGION_CURRENCY[regionOf(l) ?? '']
    if (c) return c
  }
  for (const l of locales) {
    const c = REGION_CURRENCY[regionOf(l) ?? '']
    if (c) return c
  }
  return 'USD'
}

export function formatMoney(amount: number, currency: string, locale: string): string {
  try {
    const whole = Number.isInteger(amount)
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...(whole && { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}

export type QuitStats = {
  kind: QuitKind
  // Current run of free days (the habit's streak).
  days: number
  best: number
  // Every free day logged since tracking started; money saved counts these.
  freeDays: number
  dailyCost: number | null
  currency: string | null
}

const KIND_EN: Record<QuitKind, string> = {
  smoke: 'smoke-free',
  vape: 'vape-free',
  alcohol: 'alcohol-free',
  free: 'free',
}

// English line for coach prompts: "23 days smoke-free (best 41), 60 free days total, ¥36,000 saved".
export function quitText(q: QuitStats): string {
  const saved = moneySaved(q.freeDays, q.dailyCost)
  return [
    `${q.days} ${q.days === 1 ? 'day' : 'days'} ${KIND_EN[q.kind]}${q.best > q.days ? ` (best ${q.best})` : ''}`,
    `${q.freeDays} free ${q.freeDays === 1 ? 'day' : 'days'} total`,
    saved !== null && q.currency && `${formatMoney(saved, q.currency, 'en')} saved`,
  ]
    .filter(Boolean)
    .join(', ')
}
